/**
 * Object Preload Service - Preloads database objects for all databases in a connection
 * This enables search to work immediately without manually expanding folders.
 */

import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import type { ExplorerFetchOrigin } from './explorerQueryUtils';
import { DEFAULT_PRELOAD_CONCURRENCY } from './preloadPriority';

/** The callbacks and fetcher a single database's preload needs. */
export interface PreloadDatabaseOptions {
  connectionId: string;
  /**
   * Called with `origin: 'preload'` so every listing this fan-out issues gets
   * the `exec-explorer-preload-` execution id — the one site the SQL Tools
   * adapter's per-connection gate bounds (1.4.21 review F3). A user's own
   * folder expand keeps the ungated `explorer` id.
   */
  fetchObjectsForFolder: (
    objectType: DatabaseObjectType,
    connectionId: string,
    database: string,
    origin?: ExplorerFetchOrigin,
  ) => Promise<DatabaseObject[]>;
  /**
   * Publishes one database's objects to `preloadedData` the moment that
   * database finishes, instead of making every reader wait for the slowest one.
   * Optional so callers that only want the final snapshot keep working.
   */
  /** Returns the database's new publication revision (see `readPreloadRevision`). */
  mergePreloadedObjects?: (
    connectionId: string,
    database: string,
    objects: DatabaseObject[],
    options?: { complete?: boolean },
  ) => number | void;
  /** Add objects to tree for searchability */
  addDatabaseObjects?: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  /** Called when preloading starts/ends for a database */
  onDatabasePreloadStateChange?: (connectionId: string, database: string, isPreloading: boolean) => void;
  /**
   * Called once per database after its objects have been loaded (before
   * setPreloadedData is called for the whole connection). Runs even when
   * the database has zero objects.
   */
  onDatabasePreloaded?: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  /**
   * "This preload no longer belongs to the live connection." Checked before
   * every publish, because a preload outlives what started it: a wave still
   * fanning out when the user disconnects and reconnects the same id would
   * otherwise write the OLD connection's objects — and, at the end, the OLD
   * database list with `isPreloading: false` — over the new one's.
   * See `connectionGenerations.ts`.
   */
  isStale?: () => boolean;
}

/** What one database's preload produced. */
export interface DatabasePreloadResult {
  objects: DatabaseObject[];
  /**
   * EVERY object-type listing for this database fulfilled.
   *
   * `false` means at least one listing failed (timeout, dropped connection,
   * permission), so `objects` is a partial view. Consumers of `preloadedData`
   * have no way to express partiality — `ensureObjectsLoaded` /
   * `useEnsureObjectsLoaded` / `useAvailableTables` all treat a cached entry as
   * the COMPLETE object list and skip fetching. The objects ARE published (the
   * tree and search want them); the flag travels with them so
   * `getPreloadedObjects` reports `undefined` and those callers fetch.
   */
  complete: boolean;
  /**
   * At least one object-type listing fulfilled. `false` means EVERY listing
   * rejected — a fully-failed database, which knows nothing and must overwrite
   * nothing (distinct from a genuinely-empty success, `anySucceeded && complete`
   * with no objects, which authoritatively publishes `[]`).
   */
  anySucceeded: boolean;
  /**
   * The publication revision this database's merge produced, when it published.
   * Read from the merge itself rather than a follow-up store read, so a write
   * landing in between cannot be mistaken for ours.
   */
  revision?: number;
}

export interface PreloadObjectsOptions extends PreloadDatabaseOptions {
  databases: string[];
  /**
   * Preloaded ALONE and FIRST, before the rest of `databases` fan out. Ignored
   * when it is not one of `databases` (a home database the server did not
   * list — a typo, or a database dropped since the connection was saved).
   */
  homeDatabase?: string;
  /**
   * The `databases` array written by `setPreloadedData`. Defaults to
   * `databases`, and is wider than it when this call only refreshes part of the
   * connection (`refreshDatabaseObjects` re-preloads ONE database): readers of
   * `preloadedData.databases` must still see the full accessible list.
   */
  publishedDatabases?: string[];
  /** Databases in flight at once. Defaults to {@link DEFAULT_PRELOAD_CONCURRENCY}. */
  concurrency?: number;
  setPreloadedData: (connectionId: string, data: {
    databases: string[];
    objects: Record<string, DatabaseObject[]>;
    columns: Record<string, Record<number, unknown[]>>;
    incomplete?: Record<string, true>;
    revisions?: Record<string, number>;
    /**
     * Databases whose EVERY listing rejected. Metadata-only: the store keeps
     * whatever objects it already holds (search / tree / bridge read them
     * directly) but marks the entry incomplete so `getPreloadedObjects` returns
     * `undefined` and the pickers refetch stale metadata. The value is the
     * revision the wave observed BEFORE its await, so a newer successful
     * Refresh keeps its completeness.
     */
    failed?: Record<string, number>;
  }) => void;
  /**
   * The store's publication revision for a database, read straight after this
   * wave published it. `setPreloadedData` skips any database whose revision has
   * moved on since — a per-folder Refresh that landed while the rest of the
   * wave was still running must not be reverted by the completion snapshot.
   */
  readPreloadRevision?: (connectionId: string, database: string) => number;
}

const OBJECT_TYPES_TO_PRELOAD: DatabaseObjectType[] = [
  'table',
  'view',
  'stored-procedure',
  'function',
  'package',
];

/**
 * Helper to yield to the event loop, ensuring React state updates are processed.
 * This is important for fast engines like SQLite where preloading can complete
 * before the tree state has fully propagated.
 */
function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Runs `task` over `items` with at most `limit` in flight at once. */
async function mapWithConcurrency<T>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      await task(items[index]);
      // Yield between databases: five listings land together per database and
      // each publish rewrites tree state, so a worker that loops straight into
      // the next database starves the renderer of the frame that shows the
      // last one.
      await yieldToEventLoop();
    }
  });
  await Promise.all(workers);
}

/**
 * Preloads one database's objects and publishes them everywhere a partial
 * result is useful (tree, `preloadedData`) — but never signals completion for
 * the connection, which only {@link preloadObjectsForConnection} may do.
 *
 */
async function preloadDatabaseObjects(
  {
    connectionId,
    fetchObjectsForFolder,
    mergePreloadedObjects,
    addDatabaseObjects,
    onDatabasePreloadStateChange,
    onDatabasePreloaded,
    isStale,
  }: PreloadDatabaseOptions,
  database: string,
): Promise<DatabasePreloadResult> {
  if (isStale?.()) return { objects: [], complete: false, anySucceeded: false };
  /** The revision this publish produced, when it published anything. */
  let published: number | undefined;

  // Notify preload started for this database
  onDatabasePreloadStateChange?.(connectionId, database, true);

  try {
    const dbObjects: DatabaseObject[] = [];

    // Fetch all object types for this database
    const results = await Promise.allSettled(
      OBJECT_TYPES_TO_PRELOAD.map((objectType) =>
        fetchObjectsForFolder(objectType, connectionId, database, 'preload')
      )
    );

    // Collect successful results
    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value) {
        dbObjects.push(...result.value);
      }
    });

    const complete = results.every((result) => result.status === 'fulfilled');
    const anySucceeded = results.some((result) => result.status === 'fulfilled');

    // The listings above are the slow part; the connection can have gone away
    // (and come back) while they ran. Everything below this line writes.
    if (isStale?.()) return { objects: [], complete: false, anySucceeded };

    onDatabasePreloaded?.(connectionId, database, dbObjects);

    if (dbObjects.length > 0) {
      // Both the tree and `preloadedData` get whatever came back, complete or
      // not — withholding a partial set takes the whole connection dark on any
      // engine where one listing always fails (PostgreSQL 10: both routine
      // queries filter on `pg_proc.prokind`, PG 11+). The set's completeness
      // travels WITH it instead: `mergePreloadedObjects` marks an incomplete
      // database so `getPreloadedObjects` reports `undefined` to the callers
      // that treat an entry as the definitive list (F06), while search, the
      // tree and the first-connect bridge still see the tables that arrived.
      //
      // Per-database rather than at the end so the whole fan-out is not gated
      // on the slowest database (Oracle: >30s of system schemas).
      addDatabaseObjects?.(connectionId, database, dbObjects);
      const merged = mergePreloadedObjects?.(connectionId, database, dbObjects, { complete });
      published = typeof merged === 'number' ? merged : undefined;
    }

    // A database that finished EMPTY is deliberately NOT published here. An
    // entry means "loaded, and this is everything", and the first-connect
    // bridge reads the first such entry as a definitive "this server has no
    // tables" — so an empty `postgres` publishing early would end the bridge's
    // wait while the user's application database was still loading. Empty
    // databases reach `preloadedData` in the COMPLETION snapshot instead
    // (`setPreloadedData`, which also fills in `databases`), where "no tables"
    // is unambiguous because the whole preload is done.

    return { objects: dbObjects, complete, anySucceeded, revision: published };
  } finally {
    // Notify preload ended for this database — but NOT when this preload went
    // stale mid-listing: the node id is `(connectionId, database)`, so the
    // successor connect's node is the same one, and clearing the flag here
    // would stop ITS spinner while its own preload is still running.
    if (!isStale?.()) onDatabasePreloadStateChange?.(connectionId, database, false);
  }
}

/**
 * Preloads objects (tables, views, procedures, functions) for all databases.
 * Runs in the background without blocking UI.
 *
 * Ordering: `homeDatabase` first and alone (the user's first query lands
 * there), then the rest in server order at `concurrency` at a time.
 */
export async function preloadObjectsForConnection(options: PreloadObjectsOptions): Promise<void> {
  const {
    connectionId,
    databases,
    homeDatabase,
    publishedDatabases,
    concurrency = DEFAULT_PRELOAD_CONCURRENCY,
    isStale,
    setPreloadedData,
    readPreloadRevision,
  } = options;
  const allObjects: Record<string, DatabaseObject[]> = {};
  const incomplete: Record<string, true> = {};
  /** What the store's revision was for each database right after WE published it. */
  const revisions: Record<string, number> = {};
  /** Fully-failed databases → the revision observed before their await. */
  const failed: Record<string, number> = {};

  // Yield to event loop to ensure tree state from loadDatabasesFolder has propagated.
  // This is important for fast engines (like SQLite) where database queries complete
  // almost instantly, before React has had a chance to process state updates.
  await yieldToEventLoop();

  const runOne = async (database: string): Promise<void> => {
    // Captured BEFORE the await: when this database's own listing publishes
    // nothing (an empty or failed set makes no `mergePreloadedObjects` call, so
    // no revision), the completion snapshot must compare against the revision
    // as it was when WE started — not a re-read now, which would pick up a
    // Refresh that landed DURING the await and let this stale empty result
    // overwrite the tables it just discovered.
    const baselineRevision = readPreloadRevision?.(connectionId, database) ?? 0;
    const { objects, complete, anySucceeded, revision } = await preloadDatabaseObjects(
      options,
      database,
    );

    // A fully-failed set (every listing rejected) knows nothing. It must not
    // publish an empty array — that would erase rows a previous generation, or
    // search and the bridge, still legitimately hold. But it must not leave a
    // stale entry looking AUTHORITATIVE either. So: keep the existing objects
    // (the store leaves them untouched) while marking the entry incomplete, so
    // the accessor returns `undefined` and the pickers refetch. Where there is
    // no prior entry the store publishes nothing at all. A genuinely-empty
    // SUCCESS (`anySucceeded && complete`, no rows) still publishes `[]` below.
    if (!anySucceeded && !complete) {
      failed[database] = baselineRevision;
      return;
    }

    // Recorded even when EMPTY — the completion snapshot has to be able to say
    // "this database finished with nothing" — and even when PARTIAL, with the
    // mark travelling alongside so the accessor still refuses it.
    allObjects[database] = objects;
    if (!complete) incomplete[database] = true;
    revisions[database] = revision ?? baselineRevision;
  };

  // The home database runs ALONE and FIRST. On pgsql / mssql / mysql every
  // other database costs a fresh login, so letting the wave start alongside it
  // would put the one database the user is actually in at the back of the
  // queue — and could let another database publish its objects first.
  const leadsWithHome = homeDatabase !== undefined && databases.includes(homeDatabase);
  if (leadsWithHome) {
    await runOne(homeDatabase);
    await yieldToEventLoop();
  }

  const rest = leadsWithHome ? databases.filter((db) => db !== homeDatabase) : databases;
  await mapWithConcurrency(rest, concurrency, runOne);

  // The completion signal is the most damaging write to get wrong: it publishes
  // a database LIST and flips `isPreloading` to false. A wave that outlived its
  // connect must never claim the connection is done.
  if (isStale?.()) return;

  // The snapshot is this wave's own per-database results. A successful EMPTY
  // result is authoritative and publishes `[]` — it is deliberately NOT
  // published per-database (an empty entry mid-preload would end the
  // first-connect bridge's wait), so without this the store would keep the
  // PREVIOUS generation's rows for a database whose tables were dropped. An
  // INCOMPLETE result knows nothing: it publishes what it got, marked partial.
  setPreloadedData(connectionId, {
    databases: publishedDatabases ?? databases,
    objects: allObjects,
    columns: {},
    incomplete,
    revisions,
    failed,
  });
}
